import { useCallback, useEffect, useMemo, useRef } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import StepperControls from "@components/StepperControls/StepperControls";
import { useNotification } from "@context/NotificationContext";
import { usePersistentForm } from "@hooks/usePersistentForm";
import type { Vessel } from "@services/vessel/types";
import { getVesselByIdNoCompanyIdValidation } from "@services/vesselsApi";
import { useQuery } from "@tanstack/react-query";
import { paths } from "@utils/navigation";
import { FormProvider, type SubmitErrorHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import VesselDetails from "../add/components/header/VesselDetails";
import { useUploadDocument } from "../add/hooks/useUploadDocument";
import PreNominationRequestAttachmentsSection from "./components/PreNominationRequestAttachmentsSection";
import PreNominationRequestCargoSection from "./components/PreNominationRequestCargoSection";
import PreNominationRequestConfirmationSection from "./components/PreNominationRequestConfirmationSection";
import PreNominationRequestFormSection from "./components/PreNominationRequestFormSection";
import {
  defaultValues,
  FIELDS_LANG_BASE,
  FORM_FIELDS,
  LANG_BASE,
  PRE_NOMINATION_REQUEST_UPLOAD_STORE_KEY,
  TEST_ID_BASE,
  type PreNominationRequestFormData,
  type PreNominationRequestLabels,
  type PreNominationRequestOutletContext,
} from "./constants";
import { useVesselPreNomination } from "./hooks/useVesselPreNomination";
import { getPreNominationRequestSchema } from "./schema/preNominationRequestSchema";
import { usePreNominationRequestFormStore } from "./store/usePreNominationRequestForm.store";

const SERVICE = "bff-vessel";

type PreNominationRequestLocationState =
  | {
      vessel?: {
        id?: string;
      };
      isResubmitting?: boolean;
      nominationId?: string;
    }
  | undefined;

const PreNominationRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const isClearingFormRef = useRef(false);
  const { setField, setAll, resetForm } = usePreNominationRequestFormStore();
  const selectedVesselId = (location.state as PreNominationRequestLocationState)?.vessel?.id;
  const isResubmitting =
    (location.state as PreNominationRequestLocationState)?.isResubmitting ?? false;
  const nominationId = (location.state as PreNominationRequestLocationState)?.nominationId;
  const storeSnapshot = usePreNominationRequestFormStore.getState();
  const formDefaults: PreNominationRequestFormData = storeSnapshot;
  const storeVesselId = storeSnapshot.vesselId;
  const vesselId = selectedVesselId || storeVesselId;
  const shouldRedirect = !vesselId;
  const labels = useMemo<PreNominationRequestLabels>(
    () => ({
      eta: t(`${FIELDS_LANG_BASE}.eta`),
      callSign: t(`${FIELDS_LANG_BASE}.call-sign`),
      summerDwt: t(`${FIELDS_LANG_BASE}.summer-dwt`),
      arrivalDraft: t(`${FIELDS_LANG_BASE}.arrival-draft`),
      departureDraft: t(`${FIELDS_LANG_BASE}.departure-draft`),
      hasHullProtrusion: t(`${FIELDS_LANG_BASE}.protrusion-projection-hull`),
      remarks: t(`${FIELDS_LANG_BASE}.remarks`),
      attachments: t(`${FIELDS_LANG_BASE}.attachments`),
      confirmation: t(`${FIELDS_LANG_BASE}.confirmation`),
    }),
    [t],
  );
  const schema = useMemo(() => getPreNominationRequestSchema(t, labels), [labels, t]);
  const form = usePersistentForm<PreNominationRequestFormData>({
    schema,
    defaultValues,
    storeValues: formDefaults,
    setStoreValues: setAll,
    resetStore: resetForm,
    persistKey: "pre-nomination-request-form",
    dateKeys: [FORM_FIELDS.ETA],
  });

  const updateFormData = useCallback(
    <K extends keyof PreNominationRequestFormData>(
      key: K,
      value: PreNominationRequestFormData[K],
    ) => {
      setField(key, value);
    },
    [setField],
  );

  const parcelFormContext = useMemo<PreNominationRequestOutletContext>(
    () => ({
      formData: form,
      updateFormData,
      clearErrors: form.clearErrors,
      watch: form.watch,
      setError: form.setError,
    }),
    [form, updateFormData],
  );
  const { isLoading } = useUploadDocument();
  const clearForm = useCallback(() => {
    isClearingFormRef.current = true;
    form.clear();
    usePreNominationRequestFormStore.persist.clearStorage();
    useUploadStore.getState().clearFilesForKey(PRE_NOMINATION_REQUEST_UPLOAD_STORE_KEY);
  }, [form]);
  const handleSubmitSuccess = () => {
    notify({
      title: t(`${LANG_BASE}.notifications.submitted-success-title`),
      type: "success",
    });
    clearForm();
    navigate(`/${paths.vesselNomination.index}`);
  };
  const handleSubmitError = () => {
    notify({
      title: t(`${LANG_BASE}.notifications.submitted-error-title`),
      type: "error",
    });
  };
  const { onSubmit, isSubmitPending } = useVesselPreNomination({
    formValues: form.getValues(),
    schema,
    onSuccess: handleSubmitSuccess,
    onError: handleSubmitError,
    isResubmitting,
    nominationId,
  });

  const onValid = async () => {
    if (isLoading) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    await onSubmit();
  };

  const onInvalid: SubmitErrorHandler<PreNominationRequestFormData> = () => {
    notify({
      title: t(`${LANG_BASE}.notifications.submitted-error-title`),
      description: t(`${LANG_BASE}.notifications.submitted-error-description`),
      type: "error",
    });
  };

  const cancel = () => {
    clearForm();
    navigate(`/${paths.vesselNomination.index}`);
  };

  const complete = () => {
    form.handleSubmit(onValid, onInvalid)();
  };

  useEffect(() => {
    if (isClearingFormRef.current) return;

    if (selectedVesselId && selectedVesselId !== storeVesselId) {
      setField("vesselId", selectedVesselId);
      if (form.getValues(FORM_FIELDS.VESSEL_ID) !== selectedVesselId) {
        form.setValue(FORM_FIELDS.VESSEL_ID, selectedVesselId);
      }
    }
  }, [form, selectedVesselId, setField, storeVesselId]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate(`/${paths.vesselNomination.index}`, { replace: true });
    }
  }, [navigate, shouldRedirect]);

  const { data: vesselDetails } = useQuery<Vessel>({
    queryKey: ["vessel", SERVICE, vesselId],
    queryFn: () => getVesselByIdNoCompanyIdValidation(SERVICE, vesselId as string),
    enabled: !!vesselId,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (isClearingFormRef.current) return;

    if (vesselDetails?.vesselCallSign) {
      const callSign = vesselDetails.vesselCallSign;
      if (form.getValues(FORM_FIELDS.CALL_SIGN) !== callSign) {
        form.setValue(FORM_FIELDS.CALL_SIGN, callSign);
      }
      setField(FORM_FIELDS.CALL_SIGN, callSign);
    }
  }, [form, setField, vesselDetails]);

  if (shouldRedirect) {
    return null;
  }

  return (
    <div data-testid={TEST_ID_BASE}>
      <div className="pt-8 pb-8 px-24">
        <div className="mb-6">
          <VesselDetails testId={TEST_ID_BASE} vesselDetails={vesselDetails} />
        </div>
        <FormProvider {...form}>
          <PreNominationRequestFormSection
            form={form}
            labels={labels}
            updateFormData={updateFormData}
            title={t(`${LANG_BASE}.title`)}
          />
          <div className="mt-6">
            <PreNominationRequestCargoSection formContext={parcelFormContext} />
          </div>
          <div className="mt-6">
            <PreNominationRequestAttachmentsSection
              labels={labels}
              documentsTitle={t(`${FIELDS_LANG_BASE}.attachments`)}
              updateFormData={updateFormData}
            />
          </div>
          <div className="mt-6">
            <PreNominationRequestConfirmationSection
              labels={labels}
              updateFormData={updateFormData}
            />
          </div>
        </FormProvider>

        <div
          data-testid={`${TEST_ID_BASE}-footer`}
          className="mt-6 py-6 border-t border-t-[var(--stroke-secondary-stroke,#E6EDF2)]"
        >
          <StepperControls
            cancel={cancel}
            complete={complete}
            completePending={isSubmitPending}
            completeLabelTranslationKey="stepper-controls.submit"
          />
        </div>
      </div>
    </div>
  );
};

export default PreNominationRequest;
